import { useContext, useEffect, useState } from 'react';
import { FiArrowUp, FiArrowDown, FiList, FiRotateCcw, FiMenu } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AuthContext } from '../auth/AuthContext';
import { useNavItems } from './NavItemsContext';
import { getStoredDepartmentView } from './DepartmentSwitcher';
import { DEFAULT_DEPARTMENT, normalizeDepartment } from '../constants/departments';
import {
  getStoredNavOrder,
  setStoredNavOrder,
  clearStoredNavOrder,
} from '../utils/navOrderPreference';
import ReorderableList from './ui/ReorderableList';
import { COLORS as C, RADIUS } from '../constants/ui';

/**
 * Personal sidebar ordering, for everyone.
 *
 * Reorder only — no show/hide, no rename. Those belong to the department
 * config and are an admin's call. The list here is exactly what the sidebar
 * currently renders (read from NavItemsContext), so a person can only permute
 * menu entries they already have; nothing here can surface a hidden page.
 *
 * Saved to this browser rather than the server: it's a personal preference,
 * so it applies instantly and needs nobody's approval.
 */
export default function MenuOrderPreference() {
  const { user } = useContext(AuthContext);
  const { navItems } = useNavItems();
  const department = normalizeDepartment(
    getStoredDepartmentView() || user?.department || DEFAULT_DEPARTMENT,
  );

  // Work on paths; labels come from the live nav list so a department rename
  // shows up here too.
  const [order, setOrder] = useState([]);

  useEffect(() => {
    const livePaths = navItems.map((i) => i.path);
    const saved = getStoredNavOrder(department);
    if (!saved) {
      setOrder(livePaths);
      return;
    }
    // Keep only what's currently visible, then append anything new.
    const visible = new Set(livePaths);
    const kept = saved.filter((p) => visible.has(p));
    const added = livePaths.filter((p) => !kept.includes(p));
    setOrder([...kept, ...added]);
  }, [navItems, department]);

  const labelFor = (path) => navItems.find((i) => i.path === path)?.label || path;
  const iconFor = (path) => navItems.find((i) => i.path === path)?.icon;

  const commit = (next) => {
    setOrder(next);
    setStoredNavOrder(department, next);
  };

  const reset = () => {
    clearStoredNavOrder(department);
    setOrder(navItems.map((i) => i.path));
    toast.success('Menu order reset');
  };

  if (navItems.length === 0) return null;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: RADIUS.card,
        padding: '1.5rem',
        border: `1px solid ${C.line}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: C.ink }}>
          <FiList size={20} />
          <span>Menu Order</span>
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            background: 'none',
            border: `1px solid ${C.line}`,
            borderRadius: RADIUS.control,
            padding: '5px 10px',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: C.inkSoft,
            cursor: 'pointer',
          }}
        >
          <FiRotateCcw size={13} /> Reset
        </button>
      </div>

      <p style={{ fontSize: '0.75rem', color: C.inkFaint, margin: '0 0 14px' }}>
        Drag a row to arrange the sidebar how you work, or use the arrows. This
        applies to you on this browser only — it doesn&apos;t change what your
        team sees.
        {department ? ` Saved for the ${department} view.` : ''}
      </p>

      <ReorderableList
        order={order}
        onReorder={commit}
        gap={6}
        renderItem={(path, ctx) => {
          const Icon = iconFor(path);
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: RADIUS.control,
                border: `1px solid ${C.line}`,
                background: C.sunken,
              }}
            >
              <FiMenu size={14} style={{ color: C.inkFaint, flexShrink: 0, cursor: 'grab' }} />
              <span
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: C.inkFaint,
                  width: 18,
                  textAlign: 'right',
                }}
              >
                {ctx.index + 1}
              </span>
              {Icon ? <Icon size={15} style={{ color: C.inkSoft, flexShrink: 0 }} /> : null}
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.87rem',
                  color: C.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {labelFor(path)}
              </span>
              <button
                type="button"
                onClick={ctx.moveUp}
                disabled={ctx.isFirst}
                aria-label={`Move ${labelFor(path)} up`}
                style={arrowStyle(ctx.isFirst)}
              >
                <FiArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={ctx.moveDown}
                disabled={ctx.isLast}
                aria-label={`Move ${labelFor(path)} down`}
                style={arrowStyle(ctx.isLast)}
              >
                <FiArrowDown size={14} />
              </button>
            </div>
          );
        }}
      />
    </div>
  );
}

const arrowStyle = (disabled) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: RADIUS.control,
  border: `1px solid ${C.line}`,
  background: '#fff',
  color: disabled ? C.inkFaint : C.accentInk,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  flexShrink: 0,
});
